import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lightning, X, Crown } from '@phosphor-icons/react';
import { useCompany } from '../context/CompanyContext';
import { Button } from './ui/button';

// AnnualUpsellModal — fires once per 24h on day-0 / expired trial.
// Mounted in App.js as a sibling of <TrialExpiredGate> inside ProtectedRoute
// so it overlays both the paywall (non-allowlist routes) AND any normal page
// (allowlist routes like /billing). Dismiss writes a 24-hour snooze key to
// localStorage.
const SNOOZE_KEY = 'stockaris.annual_upsell.snoozed_until';
const SNOOZE_MS = 24 * 60 * 60 * 1000;

export default function AnnualUpsellModal() {
  const { company } = useCompany();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!company || company.status !== 'pending_payment') return;
    const until = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    if (Date.now() < until) return;
    setOpen(true);
  }, [company]);

  if (!open) return null;

  const recommendedPlan = company?.plan_key && company.plan_key !== 'enterprise'
    ? company.plan_key
    : 'core';
  // Use plan price * 12 - plan price * 10 = 2× monthly = savings
  const savingsLabel = '~17% off · 2 months free';

  const handleSnooze = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    setOpen(false);
  };

  const handleAnnualUpgrade = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    setOpen(false);
    navigate(`/billing?upgrade=${recommendedPlan}&cycle=annual`);
  };

  const handleSeePlans = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    setOpen(false);
    navigate('/billing?cycle=annual');
  };

  return (
    <div
      data-testid="annual-upsell-modal"
      className="fixed inset-0 z-[120] flex items-center justify-center bg-foreground/60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-md bg-card border-2 border-foreground shadow-[8px_8px_0_0_rgba(0,0,0,0.15)]">
        <button
          type="button"
          onClick={handleSnooze}
          data-testid="annual-upsell-dismiss"
          className="absolute top-3 right-3 p-1 text-muted-foreground hover:text-foreground"
          aria-label="Dismiss"
        >
          <X size={18} weight="bold" />
        </button>
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 bg-emerald-600 flex items-center justify-center">
              <Crown size={18} weight="fill" className="text-white" />
            </div>
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-emerald-700">
              Annual offer · save 17%
            </span>
          </div>
          <h2 className="text-2xl font-bold mb-2">Lock in 2 months free.</h2>
          <p className="text-sm text-muted-foreground mb-1">
            Pay annually instead of monthly and save 17%.
          </p>
          <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest mb-5">
            {savingsLabel}
          </p>
          <div className="bg-emerald-50 border border-emerald-200 p-4 mb-5">
            <ul className="space-y-1.5 text-xs text-foreground">
              <li className="flex items-start gap-2">
                <Lightning size={12} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                Pay 10 months · keep all 12
              </li>
              <li className="flex items-start gap-2">
                <Lightning size={12} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                Same features as monthly · zero rate-limit changes
              </li>
              <li className="flex items-start gap-2">
                <Lightning size={12} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                Switch back any time at renewal
              </li>
            </ul>
          </div>
          <div className="flex flex-col gap-2">
            <Button
              onClick={handleAnnualUpgrade}
              data-testid="annual-upsell-upgrade-btn"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-none font-bold uppercase tracking-widest text-xs h-11"
            >
              <Lightning size={14} weight="fill" className="mr-2" />
              Get annual & save 17%
            </Button>
            <Button
              onClick={handleSeePlans}
              variant="outline"
              data-testid="annual-upsell-see-plans-btn"
              className="w-full rounded-none border-foreground hover:bg-muted font-bold uppercase tracking-widest text-xs h-10"
            >
              Compare annual plans
            </Button>
            <button
              onClick={handleSnooze}
              data-testid="annual-upsell-snooze-btn"
              className="text-[11px] text-muted-foreground hover:text-foreground font-mono uppercase tracking-widest mt-1"
            >
              Maybe later · remind me tomorrow
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
